/**
 * Fiche VENTE — config partagée des sections & champs (chantier « Catalogue
 * Sales » v1, maquette validée client : public/mockup-fiche-vente.html).
 *
 * Source de vérité UNIQUE pour le formulaire de création
 * (BienVenteCreateFormPage) ET la fiche d'édition (BienVenteDetailPage) :
 * ordre des sections, liste/kind des champs, clés i18n, groupes d'options,
 * champs requis, conditionnels cave/parking/garage, suggestion prix/m².
 *
 * Le RENDU (composants ui/, layout, permissions) vit dans les pages — ce
 * fichier reste .ts (zéro JSX), comme createFormFields.ts côté location.
 *
 * Colonnes DB : migration 096 ; types : src/types/bien.ts (bloc Fiche VENTE) ;
 * Zod : src/schemas/bien.ts. Les champs communs (adresse, surfaces, PEB,
 * titres/descriptions, equipements…) réutilisent les colonnes location.
 */

import type { TFunction } from "i18next";
import {
  getTypesBien,
  getTypesEnvironnement,
  getEtatsBatiment,
  getCaveOptions,
  getParkingOptions,
  getGarageOptions,
  getAnneesBati,
  CLASSES_PEB,
} from "@/data/options";
import { IMMOWEB_TEXT_LIMITS } from "@/lib/immoweb/textLimits";
import { isPublishable, type PublishGateInput } from "@/lib/publishGate";

// ─── Sections (ordre d'affichage, cf. maquette) ──────────────────────────────

export const VENTE_SECTIONS = [
  "identification",
  "general",
  // Séparé de « Bâti » le 2026-08-09 : le bloc « Bâti » est désormais STRICTEMENT
  // le même que celui de la fiche location (4 champs), et ce qui n'appartient
  // qu'à la vente — patrimoine, destination, syndic, quotités — a son bloc.
  "copropriete",
  "localisation",
  "interieur",
  "equipements",
  "exterieur",
  "raccordements",
  "finances",
  "urbanisme",
  "certificats",
  "energie",
  "proximite",
  "disponibilite",
  "pointsForts",
  "publication",
  "details",
  // Médias en DERNIER comme la maquette (galerie photos + lien YouTube).
  "medias",
] as const;
export type VenteSection = (typeof VENTE_SECTIONS)[number];

// ─── Options ─────────────────────────────────────────────────────────────────

export interface VenteOption {
  value: string;
  label: string;
}

/**
 * Tous les groupes d'options de la fiche vente, traduits. Les valeurs suivent
 * les conventions existantes du projet :
 *  - typeBien : clés neutres enum Immoweb (estateSubType) — appartements de
 *    getTypesBien + famille maison (clés propres alignées `estateSubType.house`,
 *    absentes de estateType.ts qui ne couvre que APARTMENT pour l'instant) ;
 *  - disponibilite (« Statut transaction ») : valeurs DB MAJUSCULES
 *    ("EN PRÉPARATION" / "DISPONIBLE" / "VENDU") comme la location ;
 *  - etatBatiment : valeurs FR historiques (mêmes que la location, consommées
 *    par lib/immoweb/enums.ts toBuildState) + 2 états vente (À restaurer,
 *    Gros œuvre) ;
 *  - cave/parkin/garage : mêmes 4 choix que la location (Non/Oui/Possible/Inclus) ;
 *  - statutPublication : valeurs FR historiques de la location (Brouillon /
 *    Publié / Retiré) + « Prêt à publier » (nouvelle étape vente, la V2 ne
 *    special-case que "Publié"/"Brouillon" donc sans risque) ;
 *  - le reste : slugs snake_case propres à la vente (migration 096).
 */
export function getVenteOptions(t: TFunction) {
  const ouiNon: VenteOption[] = [
    { value: "", label: "—" },
    { value: "oui", label: t("common.yes") },
    { value: "non", label: t("common.no") },
  ];
  return {
    typesBienVente: [
      ...getTypesBien(t),
      { value: "HOUSE", label: t("options.typesBien.maison") },
      { value: "VILLA", label: t("options.typesBien.villa") },
      { value: "BUNGALOW", label: t("options.typesBien.bungalow") },
      { value: "MANSION", label: t("options.typesBien.maisonDeMaitre") },
      { value: "FARMHOUSE", label: t("options.typesBien.fermette") },
      { value: "TOWN_HOUSE", label: t("options.typesBien.maisonDeVille") },
      { value: "CASTLE", label: t("vente.options.typesBien.chateau") },
      { value: "CHALET", label: t("vente.options.typesBien.chalet") },
    ],
    statutTransaction: [
      { value: "EN PRÉPARATION", label: t("vente.options.statutTransaction.enPreparation") },
      { value: "DISPONIBLE", label: t("vente.options.statutTransaction.aVendre") },
      { value: "VENDU", label: t("options.disponibilite.vendu") },
    ],
    // Étoiles = symboles neutres (même rendu dans les 3 langues), comme les
    // classes PEB littérales de CLASSES_PEB.
    evaluationInterne: [
      { value: "", label: "—" },
      { value: "5", label: "★★★★★ (5)" },
      { value: "4", label: "★★★★☆ (4)" },
      { value: "3", label: "★★★☆☆ (3)" },
      { value: "2", label: "★★☆☆☆ (2)" },
      { value: "1", label: "★☆☆☆☆ (1)" },
    ],
    etatBatimentVente: [
      ...getEtatsBatiment(t),
      { value: "À restaurer", label: t("vente.options.etatBatiment.aRestaurer") },
      { value: "Gros œuvre", label: t("vente.options.etatBatiment.grosOeuvre") },
    ],
    // Années du bâti en DÉROULANT, comme la fiche location (année courante + 2
    // jusqu'à 1900). La colonne reste du texte : c'est la saisie qu'on cadre,
    // pas le stockage.
    anneesBati: getAnneesBati(t),
    ouiNon,
    ouiNonNonApplicable: [
      ...ouiNon,
      { value: "non_applicable", label: t("vente.options.nonApplicable") },
    ],
    destinationBatiment: [
      { value: "", label: "—" },
      { value: "prive_habitation", label: t("vente.options.destinationBatiment.prive_habitation") },
      { value: "bureaux", label: t("vente.options.destinationBatiment.bureaux") },
      { value: "commerces", label: t("vente.options.destinationBatiment.commerces") },
      { value: "immeuble_rapport", label: t("vente.options.destinationBatiment.immeuble_rapport") },
      { value: "profession_liberale", label: t("vente.options.destinationBatiment.profession_liberale") },
    ],
    region: [
      { value: "bruxelles", label: t("biens.regions.bruxelles") },
      { value: "flandre", label: t("biens.regions.flandre") },
      { value: "wallonie", label: t("biens.regions.wallonie") },
    ],
    typeEnvironnement: getTypesEnvironnement(t),
    typeCuisine: [
      { value: "", label: "—" },
      { value: "non_equipee", label: t("vente.options.typeCuisine.non_equipee") },
      { value: "rangements", label: t("vente.options.typeCuisine.rangements") },
      { value: "semi_equipee", label: t("vente.options.typeCuisine.semi_equipee") },
      { value: "equipee", label: t("vente.options.typeCuisine.equipee") },
    ],
    chassisMateriau: [
      { value: "", label: "—" },
      { value: "pvc", label: t("vente.options.chassisMateriau.pvc") },
      { value: "bois", label: t("vente.options.chassisMateriau.bois") },
      { value: "aluminium", label: t("vente.options.chassisMateriau.aluminium") },
      { value: "mixte", label: t("vente.options.chassisMateriau.mixte") },
    ],
    chauffageType: [
      { value: "", label: "—" },
      { value: "individuel", label: t("vente.options.chauffageType.individuel") },
      { value: "collectif", label: t("vente.options.chauffageType.collectif") },
    ],
    chauffageEnergie: [
      { value: "", label: "—" },
      { value: "gaz", label: t("vente.options.chauffageEnergie.gaz") },
      { value: "electrique", label: t("vente.options.chauffageEnergie.electrique") },
      { value: "accumulateurs", label: t("vente.options.chauffageEnergie.accumulateurs") },
      { value: "mazout", label: t("vente.options.chauffageEnergie.mazout") },
      { value: "chauffage_sol", label: t("vente.options.chauffageEnergie.chauffage_sol") },
      { value: "pompe_chaleur", label: t("vente.options.chauffageEnergie.pompe_chaleur") },
      { value: "bois_pellets", label: t("vente.options.chauffageEnergie.bois_pellets") },
    ],
    orientation: [
      { value: "", label: "—" },
      { value: "nord", label: t("vente.options.orientation.nord") },
      { value: "sud", label: t("vente.options.orientation.sud") },
      { value: "est", label: t("vente.options.orientation.est") },
      { value: "ouest", label: t("vente.options.orientation.ouest") },
      { value: "nord_est", label: t("vente.options.orientation.nord_est") },
      { value: "nord_ouest", label: t("vente.options.orientation.nord_ouest") },
      { value: "sud_est", label: t("vente.options.orientation.sud_est") },
      { value: "sud_ouest", label: t("vente.options.orientation.sud_ouest") },
    ],
    cave: getCaveOptions(t),
    parking: getParkingOptions(t),
    garage: getGarageOptions(t),
    egouts: [
      { value: "", label: "—" },
      { value: "tout_a_legout", label: t("vente.options.egouts.tout_a_legout") },
      { value: "fosse_septique", label: t("vente.options.egouts.fosse_septique") },
    ],
    citerneMazout: [
      { value: "", label: "—" },
      { value: "conforme", label: t("vente.options.citerneMazout.conforme") },
      { value: "non_conforme", label: t("vente.options.citerneMazout.non_conforme") },
      { value: "pas_de_citerne", label: t("vente.options.citerneMazout.pas_de_citerne") },
      { value: "non_specifie", label: t("vente.options.citerneMazout.non_specifie") },
    ],
    destinationUrbanistique: [
      { value: "", label: "—" },
      { value: "zone_residentielle", label: t("vente.options.destinationUrbanistique.zone_residentielle") },
      { value: "zone_habitat_rural", label: t("vente.options.destinationUrbanistique.zone_habitat_rural") },
      { value: "zone_agricole", label: t("vente.options.destinationUrbanistique.zone_agricole") },
      { value: "zone_activite_economique", label: t("vente.options.destinationUrbanistique.zone_activite_economique") },
      { value: "zone_loisirs", label: t("vente.options.destinationUrbanistique.zone_loisirs") },
      { value: "zone_naturelle", label: t("vente.options.destinationUrbanistique.zone_naturelle") },
      { value: "autre", label: t("common.other") },
    ],
    zoneInondable: [
      { value: "", label: "—" },
      { value: "pas_zone_inondable", label: t("vente.options.zoneInondable.pas_zone_inondable") },
      { value: "zone_inondable", label: t("vente.options.zoneInondable.zone_inondable") },
      { value: "zone_inondable_potentielle", label: t("vente.options.zoneInondable.zone_inondable_potentielle") },
    ],
    // Scores littéraux (comme CLASSES_PEB) — identiques dans les 3 langues.
    pgScore: [
      { value: "", label: "—" },
      { value: "A", label: "A" },
      { value: "B", label: "B" },
      { value: "C", label: "C" },
      { value: "D", label: "D" },
    ],
    mesureJudiciaire: [
      { value: "", label: "—" },
      { value: "oui", label: t("common.yes") },
      { value: "non_aucune", label: t("vente.options.mesureJudiciaire.non_aucune") },
    ],
    attestationSol: [
      { value: "", label: "—" },
      { value: "disponible", label: t("vente.options.attestationSol.disponible") },
      { value: "non_disponible", label: t("vente.options.attestationSol.non_disponible") },
      { value: "non_applicable", label: t("vente.options.nonApplicable") },
    ],
    classesPeb: CLASSES_PEB,
    renovationObligatoire: [
      ...ouiNon,
      { value: "non_communique", label: t("vente.options.nonCommunique") },
    ],
    seraDispoPour: [
      { value: "", label: "—" },
      { value: "a_lacte", label: t("vente.options.seraDispoPour.a_lacte") },
      { value: "a_convenir", label: t("vente.options.seraDispoPour.a_convenir") },
      { value: "immediatement", label: t("vente.options.seraDispoPour.immediatement") },
      { value: "a_la_livraison", label: t("vente.options.seraDispoPour.a_la_livraison") },
      { value: "date_precise", label: t("vente.options.seraDispoPour.date_precise") },
    ],
    statutPublication: [
      { value: "Brouillon", label: t("biens.statutPublicationOptions.brouillon") },
      { value: "Prêt à publier", label: t("publications.pretAPublier") },
      { value: "Publié", label: t("biens.statutPublicationOptions.publie") },
      { value: "Retiré", label: t("biens.statutPublicationOptions.retire") },
    ],
    photosOk: [
      { value: "", label: "—" },
      { value: "pas_de_photo", label: t("biens.photosOptions.pas_de_photo") },
      { value: "suffisant", label: t("biens.photosOptions.suffisant") },
      { value: "bien", label: t("biens.photosOptions.bien") },
      { value: "tres_bien", label: t("biens.photosOptions.tres_bien") },
    ],
    videoOk: [
      { value: "", label: "—" },
      { value: "pas_de_video", label: t("biens.videoOptions.pas_de_video") },
      { value: "suffisant", label: t("biens.videoOptions.suffisant") },
      { value: "bien", label: t("biens.videoOptions.bien") },
      { value: "tres_bien", label: t("biens.videoOptions.tres_bien") },
    ],
    visibiliteImmoweb: [
      { value: "", label: "—" },
      { value: "basique", label: t("vente.options.visibiliteImmoweb.basique") },
      { value: "large", label: t("vente.options.visibiliteImmoweb.large") },
      { value: "extra_large", label: t("vente.options.visibiliteImmoweb.extra_large") },
      { value: "a_la_une", label: t("vente.options.visibiliteImmoweb.a_la_une") },
    ],
  } satisfies Record<string, VenteOption[]>;
}
export type VenteOptionsId = keyof ReturnType<typeof getVenteOptions>;

// ─── Champs ──────────────────────────────────────────────────────────────────

export type VenteFieldKind =
  | "text"       // FormField (codePostal/ville/prixM2 : comportements spéciaux gérés par les pages)
  | "select"     // SelectField custom (optionsId requis)
  | "boolSelect" // SelectField oui/non sur colonne boolean (meuble)
  | "date"       // DateField
  | "textarea"   // TextAreaField
  | "freetext"   // FreeTextAutocompleteField (projet)
  | "checkbox"   // Checkbox (textesOk)
  | "chambres"   // repeater SurfaceOrientation[] → chambresDetail (+ sync nbChambres)
  | "terrasses"  // repeater SurfaceOrientation[] → terrasses (+ miroir surfaceTerrasse)
  | "equipements"// multi-cases sur les tags EQUIPEMENT_RULES
  | "proximite"  // 6 lignes présence (oui/non) + distance (m) → JSONB proximite
  | "photos"     // galerie PhotoGallery (upload storage biens-photos → jsonb photos)
  | "pebPdf"     // dropzone PDF certificat PEB (PebPdfDropzone, bucket peb-certificats)
  | "collabSrs"  // déroulant Collaborateur SRS (options dynamiques useProfiles, rendu par les pages)
  | "quartier";  // déroulant Quartier, filtré par le code postal (useQuartiers, rendu par les pages)

export interface VenteFieldDef {
  /** field_key (colonne de `biens` — sert aussi aux permissions canSee/canEdit). */
  key: string;
  section: VenteSection;
  kind: VenteFieldKind;
  /** Clé i18n du label — absente pour les kinds composites (labels internes). */
  labelKey?: string;
  /** Groupe d'options (clé de getVenteOptions) pour kind "select". */
  optionsId?: VenteOptionsId;
  /** Cap dur de saisie (compteur x/max des composants ui/). */
  maxLength?: number;
  /** Champ pleine largeur dans la grille 2 colonnes de la section. */
  span2?: boolean;
}

/**
 * Layout curé — ordre des champs dans chaque section (cf. maquette).
 * La section "details" (3 cartes FR/NL/EN) n'a pas de defs : les pages la
 * rendent via VENTE_DETAILS_LANGS ci-dessous.
 */
const VENTE_LAYOUT: VenteFieldDef[] = [
  // 1. Identification
  { key: "typeBien", section: "identification", kind: "select", labelKey: "biens.type", optionsId: "typesBienVente" },
  { key: "disponibilite", section: "identification", kind: "select", labelKey: "vente.fields.statutTransaction", optionsId: "statutTransaction" },
  { key: "referenceInterne", section: "identification", kind: "text", labelKey: "biens.internalRef" },
  { key: "referenceExterne", section: "identification", kind: "text", labelKey: "biens.externalRef" },
  // Aligné sur la fiche location (demande Thibaud 2026-08-09). Droits déjà en
  // base pour le rôle vente : can_see=true, can_edit=FALSE — Sales le lit,
  // admin et C-Level l'éditent. Aucune migration.
  { key: "codeImmoweb", section: "identification", kind: "text", labelKey: "biens.codeImmoweb" },
  // Agent responsable = déroulant « Collaborateur SRS » (retour user 2026-07-13
  // soir : même déroulant et même intitulé que la fiche location). NON requis —
  // le collaborateur choisi décidera manuellement de lancer l'ELS (phase 2
  // ELS-sur-vente). La colonne texte `agent` reste en DB pour la future liste
  // d'agents Skyline (contact de l'export PDF) mais n'est plus exposée ici.
  { key: "collaborateurSrs", section: "identification", kind: "collabSrs", labelKey: "biens.collaborateurSrs" },
  { key: "evaluationInterne", section: "identification", kind: "select", labelKey: "vente.fields.evaluationInterne", optionsId: "evaluationInterne" },
  { key: "projet", section: "identification", kind: "freetext", labelKey: "biens.projet" },
  // 2. Général & construction
  { key: "anneeConstruction", section: "general", kind: "select", labelKey: "biens.anneeConstruction", optionsId: "anneesBati" },
  { key: "anneeRenovation", section: "general", kind: "select", labelKey: "biens.anneeRenovation", optionsId: "anneesBati" },
  { key: "etatBatiment", section: "general", kind: "select", labelKey: "biens.buildingCondition", optionsId: "etatBatimentVente" },
  { key: "nbFacades", section: "general", kind: "text", labelKey: "biens.nbFacades" },
  // 2.bis Destination & copropriété — propre à la vente.
  { key: "destinationBatiment", section: "copropriete", kind: "select", labelKey: "vente.fields.destinationBatiment", optionsId: "destinationBatiment" },
  { key: "patrimoineProtege", section: "copropriete", kind: "select", labelKey: "vente.fields.patrimoineProtege", optionsId: "ouiNonNonApplicable" },
  { key: "syndic", section: "copropriete", kind: "select", labelKey: "vente.fields.syndic", optionsId: "ouiNon" },
  { key: "partiesCopropriete", section: "copropriete", kind: "text", labelKey: "vente.fields.partiesCopropriete" },
  // 3. Localisation
  { key: "adresse", section: "localisation", kind: "text", labelKey: "common.address", span2: true },
  { key: "propertyBox", section: "localisation", kind: "text", labelKey: "biens.propertyBox" },
  { key: "codePostal", section: "localisation", kind: "text", labelKey: "common.postalCode" },
  { key: "ville", section: "localisation", kind: "text", labelKey: "common.city" },
  { key: "region", section: "localisation", kind: "select", labelKey: "common.region", optionsId: "region" },
  // ⚠️ La clé de PERMISSION est `quartier_id` (c'est elle qui est seedée en
  // base), mais la valeur se lit et s'écrit dans `quartier` — même dissociation
  // que sur la fiche location. Droits déjà présents pour le rôle vente.
  { key: "quartier_id", section: "localisation", kind: "quartier", labelKey: "biens.quartier" },
  { key: "etage", section: "localisation", kind: "text", labelKey: "biens.etage" },
  { key: "etagesTotal", section: "localisation", kind: "text", labelKey: "biens.etagesTotal" },
  { key: "typeEnvironnement", section: "localisation", kind: "select", labelKey: "biens.environmentType", optionsId: "typeEnvironnement", span2: true },
  // 4. Intérieur
  { key: "surfaceHabitable", section: "interieur", kind: "text", labelKey: "biens.surfaceHabitable" },
  { key: "surfaceSalon", section: "interieur", kind: "text", labelKey: "biens.surfaceSalon" },
  { key: "typeCuisine", section: "interieur", kind: "select", labelKey: "vente.fields.typeCuisine", optionsId: "typeCuisine" },
  // Salle à manger (Immoweb structure.building.diningRoom) — comparaison
  // sandbox / WHISE GP_C6.6, 2026-09-16. Fiche vente seule (décision Thibaud).
  { key: "salleAManger", section: "interieur", kind: "select", labelKey: "vente.fields.salleAManger", optionsId: "ouiNon" },
  { key: "surfaceCuisine", section: "interieur", kind: "text", labelKey: "biens.surfaceCuisine" },
  // `nbChambres` est un critère du garde-fou de publication (082). Il était
  // uniquement DÉRIVÉ du répéteur ci-dessous, donc un studio — zéro chambre,
  // donc zéro ligne — ne pouvait jamais être publié : aucun écran ne
  // permettait de saisir la valeur. Champ manuel rétabli ; le répéteur
  // continue de le synchroniser quand on détaille les chambres.
  { key: "nbChambres", section: "interieur", kind: "text", labelKey: "biens.nbChambres" },
  { key: "chambresDetail", section: "interieur", kind: "chambres", span2: true },
  { key: "nbSdb", section: "interieur", kind: "text", labelKey: "biens.nbSdb" },
  { key: "nbDouches", section: "interieur", kind: "text", labelKey: "biens.nbDouches" },
  { key: "nbToilettes", section: "interieur", kind: "text", labelKey: "biens.nbToilettes" },
  { key: "meuble", section: "interieur", kind: "boolSelect", labelKey: "biens.meuble" },
  // doubleVitrage retiré d'ici (2026-09-11, décision Thibaud) : doublon avec le
  // tag "Double vitrage" de la section Équipements juste en dessous — un seul
  // endroit gardé, comme côté location. Colonne DB / Zod / field_permissions
  // laissés en l'état (inoffensifs, pas de migration nécessaire).
  { key: "chassisMateriau", section: "interieur", kind: "select", labelKey: "vente.fields.chassisMateriau", optionsId: "chassisMateriau" },
  { key: "chauffageType", section: "interieur", kind: "select", labelKey: "vente.fields.chauffageType", optionsId: "chauffageType" },
  { key: "chauffageEnergie", section: "interieur", kind: "select", labelKey: "vente.fields.chauffageEnergie", optionsId: "chauffageEnergie", span2: true },
  // 5. Installations (equipements[] — tags EXACTS de EQUIPEMENT_RULES)
  { key: "equipements", section: "equipements", kind: "equipements", span2: true },
  // 6. Extérieur
  { key: "terrasses", section: "exterieur", kind: "terrasses", span2: true },
  { key: "surfaceJardin", section: "exterieur", kind: "text", labelKey: "biens.surfaceGarden" },
  { key: "orientationJardin", section: "exterieur", kind: "select", labelKey: "vente.fields.orientationJardin", optionsId: "orientation" },
  { key: "caveDisponible", section: "exterieur", kind: "select", labelKey: "biens.cellarAvailable", optionsId: "cave" },
  { key: "caveNumero", section: "exterieur", kind: "text", labelKey: "biens.cellarNumber" },
  { key: "parkingDisponible", section: "exterieur", kind: "select", labelKey: "biens.parkingAvailable", optionsId: "parking" },
  { key: "parkingNumero", section: "exterieur", kind: "text", labelKey: "biens.parkingNumber" },
  { key: "garageDisponible", section: "exterieur", kind: "select", labelKey: "biens.garageAvailable", optionsId: "garage" },
  { key: "garageNumero", section: "exterieur", kind: "text", labelKey: "biens.garageNumber" },
  // 7. Raccordements
  { key: "egouts", section: "raccordements", kind: "select", labelKey: "vente.fields.egouts", optionsId: "egouts" },
  { key: "raccordElectricite", section: "raccordements", kind: "select", labelKey: "vente.fields.raccordElectricite", optionsId: "ouiNon" },
  { key: "raccordGaz", section: "raccordements", kind: "select", labelKey: "vente.fields.raccordGaz", optionsId: "ouiNon" },
  { key: "raccordEau", section: "raccordements", kind: "select", labelKey: "vente.fields.raccordEau", optionsId: "ouiNon" },
  { key: "citerneMazout", section: "raccordements", kind: "select", labelKey: "vente.fields.citerneMazout", optionsId: "citerneMazout" },
  { key: "compteurSepElec", section: "raccordements", kind: "select", labelKey: "vente.fields.compteurSepElec", optionsId: "ouiNon" },
  { key: "compteurSepGaz", section: "raccordements", kind: "select", labelKey: "vente.fields.compteurSepGaz", optionsId: "ouiNon" },
  { key: "compteurSepEau", section: "raccordements", kind: "select", labelKey: "vente.fields.compteurSepEau", optionsId: "ouiNon" },
  { key: "borneVehiculeElec", section: "raccordements", kind: "select", labelKey: "vente.fields.borneVehiculeElec", optionsId: "ouiNon" },
  // 8. Finances
  { key: "prix", section: "finances", kind: "text", labelKey: "vente.fields.prix" },
  { key: "prixM2", section: "finances", kind: "text", labelKey: "vente.fields.prixM2" },
  { key: "masquerPrix", section: "finances", kind: "select", labelKey: "vente.fields.masquerPrix", optionsId: "ouiNon" },
  { key: "immeubleRapport", section: "finances", kind: "select", labelKey: "vente.fields.immeubleRapport", optionsId: "ouiNon" },
  { key: "revenuCadastral", section: "finances", kind: "text", labelKey: "vente.fields.revenuCadastral" },
  { key: "rcIndexe", section: "finances", kind: "text", labelKey: "vente.fields.rcIndexe" },
  { key: "precompteImmobilier", section: "finances", kind: "text", labelKey: "vente.fields.precompteImmobilier" },
  { key: "sousRegimeTva", section: "finances", kind: "select", labelKey: "vente.fields.sousRegimeTva", optionsId: "ouiNon" },
  // Sous option : overlay pause de vitrine (trigger DB 084 dépublie/republie,
  // guard 087 nettoie hors DISPONIBLE — pas de logique client nécessaire).
  // Placé dans Finances le 2026-08-09 : une mise sous option est un état de
  // NÉGOCIATION, il se lit avec le prix, pas avec les références du bien.
  // Sous option : déplacée de Finances vers Publication (onglet
  // Commercialisation) — demande Thibaud 2026-09-16 : c'est un état de mise
  // en ligne (le bien sort de la vitrine), pas une donnée de prix.
  { key: "sousOption", section: "publication", kind: "checkbox", labelKey: "biens.sousOption.label" },
  { key: "loyerHorsCharges", section: "finances", kind: "text", labelKey: "vente.fields.loyerHorsCharges", span2: true },
  { key: "dernierLoyer", section: "finances", kind: "text", labelKey: "vente.fields.dernierLoyer" },
  { key: "dernierLoyerDate", section: "finances", kind: "date", labelKey: "vente.fields.dernierLoyerDate" },
  // 9. Urbanisme & risques
  { key: "permisConstruire", section: "urbanisme", kind: "select", labelKey: "vente.fields.permisConstruire", optionsId: "ouiNon" },
  { key: "planCadastral", section: "urbanisme", kind: "select", labelKey: "vente.fields.planCadastral", optionsId: "ouiNon" },
  { key: "permisLotissement", section: "urbanisme", kind: "select", labelKey: "vente.fields.permisLotissement", optionsId: "ouiNon" },
  { key: "droitPreemption", section: "urbanisme", kind: "select", labelKey: "vente.fields.droitPreemption", optionsId: "ouiNon" },
  { key: "droitPreferenceLocataire", section: "urbanisme", kind: "select", labelKey: "vente.fields.droitPreferenceLocataire", optionsId: "ouiNon" },
  { key: "infractionUrbanistique", section: "urbanisme", kind: "select", labelKey: "vente.fields.infractionUrbanistique", optionsId: "ouiNon" },
  { key: "destinationUrbanistique", section: "urbanisme", kind: "select", labelKey: "vente.fields.destinationUrbanistique", optionsId: "destinationUrbanistique", span2: true },
  { key: "zoneInondable", section: "urbanisme", kind: "select", labelKey: "vente.fields.zoneInondable", optionsId: "zoneInondable" },
  { key: "certifInondation", section: "urbanisme", kind: "select", labelKey: "vente.fields.certifInondation", optionsId: "ouiNon" },
  { key: "pScore", section: "urbanisme", kind: "select", labelKey: "vente.fields.pScore", optionsId: "pgScore" },
  { key: "gScore", section: "urbanisme", kind: "select", labelKey: "vente.fields.gScore", optionsId: "pgScore" },
  { key: "registreFlamandConsulte", section: "urbanisme", kind: "select", labelKey: "vente.fields.registreFlamandConsulte", optionsId: "ouiNon", span2: true },
  { key: "mesureJudiciaire", section: "urbanisme", kind: "select", labelKey: "vente.fields.mesureJudiciaire", optionsId: "mesureJudiciaire", span2: true },
  // 10. Certificats & amiante
  { key: "certificatAmiante", section: "certificats", kind: "select", labelKey: "vente.fields.certificatAmiante", optionsId: "ouiNon" },
  { key: "construitAvant2001", section: "certificats", kind: "select", labelKey: "vente.fields.construitAvant2001", optionsId: "ouiNon" },
  { key: "certificatAmianteDate", section: "certificats", kind: "date", labelKey: "vente.fields.certificatAmianteDate" },
  { key: "attestationSol", section: "certificats", kind: "select", labelKey: "vente.fields.attestationSol", optionsId: "attestationSol" },
  // 11. Énergie / PEB
  { key: "classePeb", section: "energie", kind: "select", labelKey: "biens.pebClass", optionsId: "classesPeb" },
  { key: "consoKwh", section: "energie", kind: "text", labelKey: "biens.consumption" },
  { key: "consoTheorique", section: "energie", kind: "text", labelKey: "vente.fields.consoTheorique" },
  { key: "emissionCo2", section: "energie", kind: "text", labelKey: "biens.co2Emission" },
  { key: "pebMin", section: "energie", kind: "text", labelKey: "vente.fields.pebMin" },
  { key: "pebMax", section: "energie", kind: "text", labelKey: "vente.fields.pebMax" },
  { key: "certificatPeb", section: "energie", kind: "text", labelKey: "biens.pebCertificate", span2: true },
  // Dépôt du PDF du certificat (cf. maquette : dropzone sous le N° unique).
  // Un seul PDF par bien — colonne pebCertificatPdf (URL publique), même
  // mécanisme que le prestataire PEB (hook useBienPebPdfUpload).
  { key: "pebCertificatPdf", section: "energie", kind: "pebPdf", labelKey: "vente.fields.pebCertificatPdf", span2: true },
  { key: "pebDate", section: "energie", kind: "date", labelKey: "vente.fields.pebDate" },
  { key: "pebFinLe", section: "energie", kind: "date", labelKey: "vente.fields.pebFinLe" },
  { key: "inspectionElecValide", section: "energie", kind: "select", labelKey: "vente.fields.inspectionElecValide", optionsId: "ouiNon" },
  { key: "certifElecDate", section: "energie", kind: "date", labelKey: "vente.fields.certifElecDate" },
  { key: "renovationObligatoire", section: "energie", kind: "select", labelKey: "vente.fields.renovationObligatoire", optionsId: "renovationObligatoire", span2: true },
  // 12. À proximité
  { key: "proximite", section: "proximite", kind: "proximite", span2: true },
  // 13. Disponibilité — ⚠️ seraDispoPour RÉUTILISE la colonne location (date en
  // location, slug en vente). On stocke le SLUG du select.
  { key: "seraDispoPour", section: "disponibilite", kind: "select", labelKey: "vente.fields.seraDispoPour", optionsId: "seraDispoPour" },
  { key: "venteDisponibiliteDate", section: "disponibilite", kind: "date", labelKey: "vente.fields.venteDisponibiliteDate" },
  // 14. Points forts
  { key: "pointsForts", section: "pointsForts", kind: "textarea", labelKey: "biens.pointsFortsLabel", span2: true },
  // 15. Publication
  { key: "statutPublication", section: "publication", kind: "select", labelKey: "biens.statutPublication", optionsId: "statutPublication" },
  { key: "photosOk", section: "publication", kind: "select", labelKey: "biens.photosOk", optionsId: "photosOk" },
  { key: "videoOk", section: "publication", kind: "select", labelKey: "biens.videoOk", optionsId: "videoOk" },
  { key: "textesOk", section: "publication", kind: "checkbox", labelKey: "biens.textesOk" },
  { key: "alerteTexte", section: "publication", kind: "date", labelKey: "biens.alerteTexte" },
  { key: "visibiliteImmoweb", section: "publication", kind: "select", labelKey: "vente.fields.visibiliteImmoweb", optionsId: "visibiliteImmoweb" },
  // 16. Médias (dernière section, comme la maquette)
  { key: "videoYoutubeUrl", section: "medias", kind: "text", labelKey: "biens.videoYoutubeUrl", span2: true },
  { key: "photos", section: "medias", kind: "photos", span2: true },
];

/** N° cave/parking/garage : visibles seulement si le "Disponible" parent est
 *  Oui ou Inclus — même logique que la location (getConditionalMailingFields),
 *  SANS les champs loyer (hors périmètre vente). */
const CONDITIONAL_NUMERO: Record<string, string> = {
  caveNumero: "caveDisponible",
  parkingNumero: "parkingDisponible",
  garageNumero: "garageDisponible",
};

/** Liste des champs à rendre pour l'état courant du brouillon/de la fiche. */
export function getVenteFormFields(draft: Record<string, unknown>): VenteFieldDef[] {
  return VENTE_LAYOUT.filter((f) => {
    const parent = CONDITIONAL_NUMERO[f.key];
    if (!parent) return true;
    const v = typeof draft[parent] === "string" ? (draft[parent] as string) : "";
    return v === "Oui" || v === "Inclus";
  });
}

// ─── Requis (validation minimale vente) ──────────────────────────────────────

export const VENTE_REQUIRED_FIELDS = ["adresse", "codePostal", "ville", "typeBien", "prix"] as const;

/**
 * Saisir une « Date de disponibilité » vaut choix « À une date précise » dans
 * le déroulant « Disponibilité ». Sans ça (retour Thibaud 2026-09-16) : le
 * déroulant reste vide, le garde-fou de publication (client `isPublishable`
 * + SQL `bien_publish_missing`) bloque sur « sera dispo pour », et le mapper
 * Immoweb ne lit la date que sous le slug `date_precise`. Vider la date ne
 * touche pas au déroulant : l'utilisateur choisit alors lui-même.
 * Partagé par la fiche (BienVenteDetailPage) et la création
 * (BienVenteCreateFormPage).
 */
export function venteDispoDatePatch(value: string): {
  venteDisponibiliteDate: string;
  seraDispoPour?: "date_precise";
} {
  return value.trim() ? { venteDisponibiliteDate: value, seraDispoPour: "date_precise" } : { venteDisponibiliteDate: value };
}

/**
 * Map fieldKey → "required" pour les champs vides, consommée par `missingHint`
 * des composants ui/ (bordure + libellé rouges).
 *
 * Deux sources cumulées :
 *  1. VENTE_REQUIRED_FIELDS — validation minimale de la fiche (l'adresse et le
 *     code postal n'entrent pas dans le garde-fou de publication : la commune
 *     seule suffit à publier, décision client 2026-06-11) ;
 *  2. le GARDE-FOU DE PUBLICATION (`isPublishable`) — les critères qui
 *     empêchent réellement de mettre le bien en ligne.
 *
 * Avant (retour PO 2026-08-07) seule la source 1 était branchée : le bien
 * était refusé à la publication par un toast, mais rien à l'écran n'indiquait
 * QUELS champs remplir ni OÙ ils se trouvaient. La fiche location, elle,
 * colore ses champs manquants depuis toujours.
 *
 * Les clés du gate qui ne correspondent pas à un champ acceptant `missingHint`
 * (photos, textesOk, sousOption) sont quand même posées : les composants qui
 * ne gèrent pas la prop l'ignorent, et le bandeau les liste de toute façon.
 *
 * `includePublishGate` est FAUX par défaut : au FORMULAIRE DE CRÉATION les
 * critères de publication n'ont pas de sens (on ne peut pas déposer de photo
 * avant que le bien existe, les textes ne sont pas encore écrits) — les
 * afficher en rouge ne serait que du bruit. Seule la fiche d'édition les
 * active.
 */
export function getVenteMissingHints(
  draft: Record<string, unknown>,
  includePublishGate = false,
): Map<string, "required"> {
  const map = new Map<string, "required">();
  for (const k of VENTE_REQUIRED_FIELDS) {
    const v = draft[k];
    const filled = typeof v === "string" ? v.trim().length > 0 : v !== null && v !== undefined;
    if (!filled) map.set(k, "required");
  }
  // « À une date précise » sans date : le garde-fou laisse passer (déroulant
  // rempli) mais rien ne partirait vers Immoweb — on encadre la date.
  if (draft.seraDispoPour === "date_precise") {
    const d = draft.venteDisponibiliteDate;
    if (!(typeof d === "string" && d.trim().length > 0)) map.set("venteDisponibiliteDate", "required");
  }
  if (includePublishGate) {
    for (const k of isPublishable(draft as PublishGateInput).missing) {
      // `sousOption` est un état délibéré, pas un champ à remplir — ne pas
      // encadrer sa case en rouge.
      if (k === "sousOption") continue;
      map.set(k, "required");
    }
  }
  return map;
}

// ─── Équipements ─────────────────────────────────────────────────────────────

/**
 * Les multi-cases du bloc « Équipements » viennent désormais de la liste
 * UNIQUE `EQUIPEMENT_TAGS` (src/data/constants.ts), partagée avec la fiche
 * location — décision Thibaud 2026-08-09. La vente n'en avait que 9 sur 12 :
 * les deux fiches écrivant la même colonne `equipements`, les trois manquantes
 * étaient invisibles et indécochables ici.
 *
 * Ré-export pour ne pas éparpiller les imports des écrans vente.
 */
export { EQUIPEMENT_TAGS } from "@/data/constants";

// ─── À proximité ─────────────────────────────────────────────────────────────

/** Points d'intérêt du JSONB `proximite` (type Proximite de types/bien.ts). */
export const PROXIMITE_POIS = ["ville", "centre", "magasins", "ecoles", "transports", "autoroute"] as const;
export type ProximitePoi = (typeof PROXIMITE_POIS)[number];

// ─── Détails multilingues (3 cartes FR/NL/EN) ────────────────────────────────

export const VENTE_DETAILS_LANGS = [
  { titreKey: "titreFr", descriptionKey: "descriptionFr", cardLabelKey: "biens.detailsFr" },
  { titreKey: "titreNl", descriptionKey: "descriptionNl", cardLabelKey: "biens.detailsNl" },
  { titreKey: "titreEn", descriptionKey: "descriptionEn", cardLabelKey: "biens.detailsEn" },
] as const;

/** Cap titre = maxLength Immoweb headline (60). */
export const VENTE_TITLE_MAX = IMMOWEB_TEXT_LIMITS.headline;
/** Cap description = limite Immoweb (2500 depuis le 2026-11, demande Thibaud ;
 *  avant : 1500). Même valeur sur la fiche location (BienDetailPage). */
export const VENTE_DESCRIPTION_MAX = IMMOWEB_TEXT_LIMITS.description;

// ─── Prix au m² (suggestion auto) ────────────────────────────────────────────

function toNum(v: unknown): number | null {
  if (typeof v !== "string" && typeof v !== "number") return null;
  const s = String(v).replace(/\s/g, "").replace(",", ".").replace(/[^\d.]/g, "");
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** round(prix / surfaceHabitable) — "" si l'un des deux est absent/invalide.
 *  Affiché en placeholder du champ prixM2 (éditable, jamais forcé). */
export function suggestPrixM2(prix: unknown, surfaceHabitable: unknown): string {
  const p = toNum(prix);
  const s = toNum(surfaceHabitable);
  if (!p || !s) return "";
  return String(Math.round(p / s));
}
